import React, { useEffect, useState, useMemo } from 'react';
import {
  Radio,
  FormControlLabel,
  FormControl,
  useMediaQuery,
  ListItem
} from '@mui/material';
import InputMask from 'react-input-mask';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CloseIcon from '@mui/icons-material/Close';

import * as Styles from './styles.js';

import { VENDEDOR_MULTIMARCA } from '../../util/perfilId.js';
import LogoIcon from '../../assets/logo_mais_vendas.png';

const ProfileUpdateModal = ({ open, onClose, onSubmit, userProfile }) => {
  const initialState = {
    instagram: { user: '', type: 'pessoal' },
    tiktok: { user: '', type: 'pessoal' },
    whatsapp: { user: '', type: 'pessoal' },
 };

  const [profileData, setProfileData] = useState(initialState);

  const hasValidChanges = useMemo(() => {
    const phone = profileData.whatsapp.user.replace(/\D/g, '');
    const phoneValid = phone.length === 0 || phone.length === 11;

    const hasAnyUser =
      profileData.instagram.user.trim() !== '' ||
      profileData.tiktok.user.trim() !== '' ||
      profileData.whatsapp.user.trim() !== '';

    return hasAnyUser && phoneValid;
  }, [profileData]);

  const typeMap = {
    pessoal: 'PERSONAL',
    comercial: 'COMMERCIAL',
  };

  useEffect(() => {
    if (open) {
      setProfileData(initialState);
    }
  }, [open]);

  const handleInputChange = (field, subfield) => e => {
    const value = e.target.value;
    setProfileData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subfield]: value,
      },
    }));
  };

  const handleSubmit = () => {
    const formattedData = {
      instagram_user: profileData.instagram.user.trim(),
      instagram_type: typeMap[profileData.instagram.type],
      tiktok_user: profileData.tiktok.user.trim(),
      tiktok_type: typeMap[profileData.tiktok.type],
      phone: profileData.whatsapp.user.replace(/\D/g, ''),
      phone_type: typeMap[profileData.whatsapp.type],
    };
    onSubmit(formattedData);
  };

  const isMobile = useMediaQuery('(max-width:600px)');
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const shouldShowAlternativeModal = userProfile?.id_perfil === VENDEDOR_MULTIMARCA;

  const renderAlternativeModal = () => (

    <Styles.BannerAlternative>
      {isMobile && (
        <Styles.StyledDialogCloseButton onClick={onClose}>
          <CloseIcon />
        </Styles.StyledDialogCloseButton>
      )}
      {!isMobile && <Styles.Logo component="img" image={LogoIcon} />}

      <Styles.TitleAlternative>VOCÊ UTILIZA REDES SOCIAIS?</Styles.TitleAlternative>
      <Styles.SubtitleAlternative>Queremos estar mais próximos de você!</Styles.SubtitleAlternative>

      <Styles.DescriptionAlternative>
        Ao compartilhar suas informações de contato, conseguimos enviar via WhatsApp conteúdos mais relevantes para o seu dia a dia, como:
      </Styles.DescriptionAlternative>

      <Styles.BenefitList>
        <ListItem disablePadding>Materiais de apoio à venda.</ListItem>
        <ListItem disablePadding>Conteúdos de produto e campanhas.</ListItem>
        <ListItem disablePadding>Convites para treinamentos.</ListItem>
        <ListItem disablePadding>Comunicações que ajudam no atendimento.</ListItem>
      </Styles.BenefitList>

      <Styles.FooterDescription>
        Tudo para facilitar sua rotina e apoiar suas vendas.{"\n"} Atualize seus dados e aproveite tudo o que preparamos pra você!
      </Styles.FooterDescription>

      <Styles.InputsContainerAlternative>
        {isMobile ? (
          ['whatsapp', 'instagram', 'tiktok'].map((rede) => (
            <Styles.StyledAccordion
              disableGutters
              key={rede}
              expanded={expanded === rede}
              onChange={handleAccordionChange(rede)}
              sx={{ width: '100%', background: 'transparent' }}
            >
              <Styles.StyledAccordionSummary
                expandIcon={expanded === rede ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                sx={{ padding: 0 }}
              >
                <span style={{ color: '#fff', fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {rede === 'whatsapp' ? 'WhatsApp' : rede}
                </span>
              </Styles.StyledAccordionSummary>
              <Styles.StyledAccordionDetails sx={{ padding: '8px 0' }}>
                <Styles.StyledRadioGroup 
                  value={profileData[rede].type} 
                  onChange={handleInputChange(rede, 'type')}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel value="pessoal" control={<Radio size="small" sx={{ color: '#fff' }} />} label="Pessoal" />
                  <FormControlLabel value="comercial" control={<Radio size="small" sx={{ color: '#fff' }} />} label="Comercial" />
                </Styles.StyledRadioGroup>
                
                {rede === 'whatsapp' ? (
                  <InputMask
                    mask="(99)99999-9999"
                    value={profileData.whatsapp.user}
                    onChange={handleInputChange('whatsapp', 'user')}
                  >
                    {(inputProps) => (
                      <Styles.StyledTextField {...inputProps} variant="outlined" placeholder="Qual o seu telefone?" fullWidth />
                    )}
                  </InputMask>
                ) : (
                  <Styles.StyledTextField
                    variant="outlined"
                    placeholder={`Qual seu @ do ${rede}?`}
                    value={profileData[rede].user}
                    onChange={handleInputChange(rede, 'user')}
                    fullWidth
                  />
                )}
              </Styles.StyledAccordionDetails>
            </Styles.StyledAccordion>
          ))
        ) : (
          ['whatsapp', 'instagram', 'tiktok'].map((rede) => (
            <Styles.InputWrapperAlternative 
              key={rede} 
              style={rede === 'tiktok' ? { gridColumn: 'span 2', maxWidth: '48%' } : {}}
            >
              <label style={{ textTransform: 'capitalize' }}>{rede}:</label>
              
              <Styles.StyledRadioGroup 
                row 
                value={profileData[rede].type} 
                onChange={handleInputChange(rede, 'type')}
              >
                <FormControlLabel value="pessoal" control={<Radio size="small" sx={{ color: '#fff' }} />} label="Pessoal" />
                <FormControlLabel value="comercial" control={<Radio size="small" sx={{ color: '#fff' }} />} label="Comercial" />
              </Styles.StyledRadioGroup>

              {rede === 'whatsapp' ? (
                <InputMask
                  mask="(99)99999-9999"
                  value={profileData.whatsapp.user}
                  onChange={handleInputChange('whatsapp', 'user')}
                >
                  {(inputProps) => (
                    <Styles.StyledTextField {...inputProps} variant="outlined" placeholder="Telefone de Whatsapp" fullWidth />
                  )}
                </InputMask>
              ) : (
                <Styles.StyledTextField
                  variant="outlined"
                  placeholder={`Seu @ do ${rede}`}
                  value={profileData[rede].user}
                  onChange={handleInputChange(rede, 'user')}
                  fullWidth
                />
              )}
            </Styles.InputWrapperAlternative>
          ))
        )}
      </Styles.InputsContainerAlternative>

      <Styles.SendButton onClick={handleSubmit} disabled={!hasValidChanges}>
        Enviar
      </Styles.SendButton>
    </Styles.BannerAlternative>
  );

  return (
    <Styles.StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {shouldShowAlternativeModal ? (
        <Styles.StyledDialogContent sx={{ padding: 0 }}>
          {renderAlternativeModal()}
        </Styles.StyledDialogContent>
      ) : (
        <Styles.StyledDialogContent sx={{ padding: isMobile ? '16px' : '24px 48px' }}>
          {isMobile && (
            <Styles.StyledDialogCloseButton onClick={onClose}>
              <CloseIcon />
            </Styles.StyledDialogCloseButton>
          )}

          {!isMobile && <Styles.Logo component="img" image={LogoIcon} />}

          <Styles.StyledDialogTitle>
            USA REDES SOCIAIS NO SEU DIA A DIA COM CLIENTES?
          </Styles.StyledDialogTitle>

          <Styles.StyledTypography>
            Queremos saber se <strong>você usa Instagram, WhatsApp ou TikTok</strong> em alguma etapa do atendimento. <br /> Se sim, <strong>preencha seus perfis abaixo</strong>. Isso nos ajudará a entender melhor a sua rotina e pensar em formas de apoiar o seu trabalho!
          </Styles.StyledTypography>

          {isMobile ? (
            ['instagram', 'tiktok', 'whatsapp'].map(rede => (
              <Styles.StyledAccordion disableGutters key={rede} expanded={expanded === rede} onChange={handleAccordionChange(rede)}>
                <Styles.StyledAccordionSummary expandIcon={expanded === rede ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}>
                  {rede.charAt(0).toUpperCase() + rede.slice(1)}
                </Styles.StyledAccordionSummary>
                <Styles.StyledAccordionDetails>
                  <FormControl fullWidth>
                    <Styles.StyledRadioGroup value={profileData[rede].type} onChange={handleInputChange(rede, 'type')}>
                      <FormControlLabel value="pessoal" control={<Radio />} label="Pessoal" />
                      <FormControlLabel value="comercial" control={<Radio />} label="Comercial" />
                    </Styles.StyledRadioGroup>
                    {rede === 'whatsapp' ? (
                      <InputMask mask="(99)99999-9999" value={profileData[rede].user} onChange={handleInputChange(rede, 'user')}>
                        {(inputProps) => (
                          <Styles.StyledTextField
                            {...inputProps}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            placeholder={`Insira seu usuário no ${rede.charAt(0).toUpperCase() + rede.slice(1)}`}
                          />
                        )}
                      </InputMask>
                    ) : (
                      <Styles.StyledTextField
                        variant="outlined"
                        value={profileData[rede].user}
                        onChange={handleInputChange(rede, 'user')}
                        fullWidth
                        margin="dense"
                        placeholder={`Insira seu usuário no ${rede.charAt(0).toUpperCase() + rede.slice(1)}`}
                      />
                    )}
                  </FormControl>
                </Styles.StyledAccordionDetails>
              </Styles.StyledAccordion>
            ))
          ) : (
            <Styles.StyledDialogInputs>
              {['instagram', 'tiktok', 'whatsapp'].map(rede => (
                <FormControl fullWidth key={rede}>
                  <Styles.StyledFormLabel>{rede.charAt(0).toUpperCase() + rede.slice(1)}:</Styles.StyledFormLabel>
                  <Styles.StyledRadioGroup row value={profileData[rede].type} onChange={handleInputChange(rede, 'type')}>
                    <FormControlLabel value="pessoal" control={<Radio />} label="Pessoal" />
                    <FormControlLabel value="comercial" control={<Radio />} label="Comercial" />
                  </Styles.StyledRadioGroup>
                  {rede === 'whatsapp' ? (
                    <InputMask mask="(99)99999-9999" value={profileData[rede].user} onChange={handleInputChange(rede, 'user')}>
                      {(inputProps) => (
                        <Styles.StyledTextField
                          {...inputProps}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          placeholder={`Insira seu usuário no ${rede.charAt(0).toUpperCase() + rede.slice(1)}`}
                        />
                      )}
                    </InputMask>
                  ) : (
                    <Styles.StyledTextField
                      variant="outlined"
                      value={profileData[rede].user}
                      onChange={handleInputChange(rede, 'user')}
                      fullWidth
                      margin="dense"
                      placeholder={`Insira seu usuário no ${rede.charAt(0).toUpperCase() + rede.slice(1)}`}
                    />
                  )}
                </FormControl>
              ))}
            </Styles.StyledDialogInputs>
          )}

          <Styles.StyledDialogActions isMobile={isMobile} sx={{ paddingBottom: '24px' }}>
            <Styles.StyledSecondaryButton fullWidth={isMobile} onClick={onClose}>
              FAZER ISSO MAIS TARDE
            </Styles.StyledSecondaryButton>
            <Styles.StyledButton fullWidth={isMobile} onClick={handleSubmit} disabled={!hasValidChanges}>
              CONFIRMAR
            </Styles.StyledButton>
          </Styles.StyledDialogActions>
        </Styles.StyledDialogContent>
      )}
    </Styles.StyledDialog>
  );
};

export default ProfileUpdateModal;